/* eslint-disable remix-react-routes/use-link-for-routes */
import { useState } from 'react'
import { json, type V2_MetaFunction } from '@remix-run/node'
import { useLoaderData } from '@remix-run/react'
import { AnimatePresence, motion } from 'framer-motion'
import { Dialog } from '@headlessui/react'
import {
	// CalendarDaysIcon,
	// CalendarDaysIcon,
	// CalendarDaysIcon,
	MicrophoneIcon,
	VideoCameraIcon,
} from '@heroicons/react/20/solid'
import { prisma } from '~/utils/db.server.ts'

export const meta: V2_MetaFunction = () => [{ title: 'Hope Point Church' }]

export async function loader() {
	// Use start of today in UTC to avoid timezone issues
	const today = new Date()
	today.setUTCHours(0, 0, 0, 0)

	const [events, message] = await Promise.all([
		prisma.events.findMany({
			where: {
				isActive: true,
				OR: [
					// Recurring events always show
					{ isRecurring: true },
					// Events with an end date: show if end date hasn't passed
					{ end: { gte: today } },
					// Events without an end date: show if start date hasn't passed
					{ end: null, start: { gte: today } },
				],
			},
			orderBy: [
				{ isRecurring: 'desc' },
				{ start: 'asc' },
			],
		}),
		prisma.message.findFirst(),
	])

	return json({ events, message })
}

const TRANSITIONS = {
	DURATION: 0.5,
	EASE: [0.32, 0.72, 0, 1],
}

export default function Index() {
	let [openLatestMessages, setOpenLatestMessages] = useState(false)
	let [openGiving, setOpenGiving] = useState(false)
	let [openNextSteps, setOpenNextSteps] = useState(false)
	let [openUpcomingEvents, setOpenUpcomingEvents] = useState(false)

	return (
		<div className="mx-auto max-w-xl">
			{/* <h2 className="pb-4 pl-6 font-display text-2xl font-semibold tracking-widest">
				What's Happening
			</h2> */}
			<div className="relative mb-6 overflow-hidden text-black">
				<div className="mx-auto max-w-xl">
					<div className="container-snap flex snap-x snap-mandatory space-x-4 overflow-x-scroll px-4 sm:space-x-6 sm:px-8">
						{/* New Here Card */}
						<div className="relative flex h-64 w-72 shrink-0 snap-center flex-col rounded-2xl bg-[#d8bfa1] p-5 shadow-lg ring-1 ring-black/5 sm:h-[20rem] sm:w-[22rem]">
							<h2 className="text-xl font-bold tracking-wide sm:text-2xl">
								Joining us for the first time?
							</h2>
							<p className="mt-3 flex-1 text-sm leading-relaxed text-stone-700 sm:text-base">
								We are so glad you are here! Click below to get connected with Hope Point.
							</p>
							<a
								href="https://hopepointchurch.churchcenter.com/people/forms/107297"
								target="_blank"
								rel="noreferrer"
								className="mt-4 inline-block self-start rounded-lg border-2 border-black bg-[#e8ddcb] px-5 py-2.5 font-display font-bold transition hover:bg-[#ded9d3]"
							>
								New Here →
							</a>
						</div>

						{/* Decision Card */}
						<div className="relative flex h-64 w-72 shrink-0 snap-center flex-col rounded-2xl bg-[#e8ddcb] p-5 shadow-lg ring-1 ring-black/5 sm:h-[20rem] sm:w-[22rem]">
							<h2 className="text-xl font-bold tracking-wide sm:text-2xl">
								I have decided!
							</h2>
							<p className="mt-3 flex-1 text-sm leading-relaxed text-stone-700 sm:text-base">
								Just say a prayer of salvation? We want to celebrate with you and walk you through your journey.
							</p>
							<a
								href="https://hopepointchurch.churchcenter.com/people/forms/555152"
								target="_blank"
								rel="noreferrer"
								className="mt-4 inline-block self-start rounded-lg border-2 border-black bg-[#ded9d3] px-5 py-2.5 font-display font-bold transition hover:bg-[#d4cfca]"
							>
								Let us know →
							</a>
						</div>

						{/* Hope Cares Card */}
						<div className="relative flex h-64 w-72 shrink-0 snap-center flex-col rounded-2xl bg-[#ded9d3] p-5 shadow-lg ring-1 ring-black/5 sm:h-[20rem] sm:w-[22rem]">
							<h2 className="text-xl font-bold tracking-wide sm:text-2xl">
								Hope Cares
							</h2>
							<p className="mt-3 flex-1 text-sm leading-relaxed text-stone-700 sm:text-base">
								Get involved in our community and make a difference in the lives of others.
							</p>
							<a
								href="https://hopepointchurch.org/hopecares"
								target="_blank"
								rel="noreferrer"
								className="mt-4 inline-block self-start rounded-lg border-2 border-black bg-[#e8ddcb] px-5 py-2.5 font-display font-bold transition hover:bg-[#ded9d3]"
							>
								Learn More →
							</a>
						</div>

						{/* Social Card */}
						<div className="relative flex h-64 w-72 shrink-0 snap-center flex-col rounded-2xl bg-[#d8bfa1] p-5 shadow-lg ring-1 ring-black/5 sm:h-[20rem] sm:w-[22rem]">
							<h2 className="text-xl font-bold tracking-wide sm:text-2xl">
								Join us on social
							</h2>
							<p className="mt-3 text-sm leading-relaxed text-stone-700 sm:text-base">
								Stay connected with our community online.
							</p>
							<div className="mt-6 flex flex-1 items-center justify-center gap-6">
								<a
									href="https://www.facebook.com/hopepointva"
									target="_blank"
									rel="noreferrer"
									className="rounded-full bg-[#e8ddcb] p-3 transition hover:bg-[#ded9d3]"
								>
									<span className="sr-only">Facebook</span>
									<svg className="h-8 w-8" fill="currentColor" viewBox="0 0 24 24">
										<path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
									</svg>
								</a>
								<a
									href="https://www.instagram.com/hopepointva/"
									target="_blank"
									rel="noreferrer"
									className="rounded-full bg-[#e8ddcb] p-3 transition hover:bg-[#ded9d3]"
								>
									<span className="sr-only">Instagram</span>
									<svg className="h-8 w-8" fill="currentColor" viewBox="0 0 24 24">
										<path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd" />
									</svg>
								</a>
								<a
									href="https://www.youtube.com/channel/UC3aFwajgm4Yxh8xvGGgVYPg"
									target="_blank"
									rel="noreferrer"
									className="rounded-full bg-[#e8ddcb] p-3 transition hover:bg-[#ded9d3]"
								>
									<span className="sr-only">YouTube</span>
									<svg className="h-8 w-8" viewBox="0 0 50 50" fill="currentColor">
										<path d="M 44.898438 14.5 C 44.5 12.300781 42.601563 10.699219 40.398438 10.199219 C 37.101563 9.5 31 9 24.398438 9 C 17.800781 9 11.601563 9.5 8.300781 10.199219 C 6.101563 10.699219 4.199219 12.199219 3.800781 14.5 C 3.398438 17 3 20.5 3 25 C 3 29.5 3.398438 33 3.898438 35.5 C 4.300781 37.699219 6.199219 39.300781 8.398438 39.800781 C 11.898438 40.5 17.898438 41 24.5 41 C 31.101563 41 37.101563 40.5 40.601563 39.800781 C 42.800781 39.300781 44.699219 37.800781 45.101563 35.5 C 45.5 33 46 29.398438 46.101563 25 C 45.898438 20.5 45.398438 17 44.898438 14.5 Z M 19 32 L 19 18 L 31.199219 25 Z" />
									</svg>
								</a>
							</div>
						</div>

						{/* Website Card */}
						<div className="relative flex h-64 w-72 shrink-0 snap-center flex-col rounded-2xl bg-[#e8ddcb] p-5 shadow-lg ring-1 ring-black/5 sm:h-[20rem] sm:w-[22rem]">
							<h2 className="text-xl font-bold tracking-wide sm:text-2xl">
								Visit our website
							</h2>
							<p className="mt-3 flex-1 text-sm leading-relaxed text-stone-700 sm:text-base">
								Need more information? Visit our full website for all the details.
							</p>
							<a
								href="https://hopepointchurch.org"
								target="_blank"
								rel="noreferrer"
								className="mt-4 inline-block self-start rounded-lg border-2 border-black bg-[#ded9d3] px-5 py-2.5 font-display font-bold transition hover:bg-[#d4cfca]"
							>
								hopepointchurch.org →
							</a>
						</div>

						{/* Home Screen Card */}
						<div className="relative flex h-64 w-72 shrink-0 snap-center flex-col rounded-2xl bg-black p-5 text-white shadow-lg sm:h-[20rem] sm:w-[22rem]">
							<h2 className="text-xl font-bold tracking-wide sm:text-2xl">
								Add to home screen
							</h2>
							<p className="mt-3 flex-1 text-sm leading-relaxed text-gray-300 sm:text-base">
								Tap{' '}
								<svg
									xmlns="http://www.w3.org/2000/svg"
									fill="none"
									viewBox="0 0 24 24"
									strokeWidth="1.5"
									stroke="currentColor"
									className="inline-block h-5 w-5"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										d="M9 8.25H7.5a2.25 2.25 0 00-2.25 2.25v9a2.25 2.25 0 002.25 2.25h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25H15m0-3l-3-3m0 0l-3 3m3-3V15"
									/>
								</svg>{' '}
								at the bottom of your mobile browser and then tap "Add to Home Screen"
							</p>
							<div className="mt-4 rounded-lg bg-white/10 px-4 py-2 text-center text-sm text-gray-300">
								Quick access anytime
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className="relative flex flex-col items-center justify-center bg-[#ded9d3] text-black sm:bg-[#ededea]">
				<div className="w-full">
					<button
						onClick={() => setOpenLatestMessages(true)}
						className="w-full"
					>
						<div className="h-28 rounded-t-xl bg-black text-white">
							<div className="mx-auto h-1 w-12 translate-y-2 bg-gray-100 opacity-20" />
							<h2 className="pt-5 font-bold uppercase tracking-widest">
								Latest Message
							</h2>
						</div>
					</button>
					<button
						onClick={() => setOpenGiving(true)}
						className="w-full -translate-y-12 pb-3"
					>
						<div className="h-28 rounded-t-xl bg-[#e8ddcb]">
							<div className="mx-auto h-1 w-12 translate-y-2 bg-black opacity-10" />
							<h2 className="pt-5 font-bold uppercase tracking-widest">
								Giving
							</h2>
						</div>
					</button>
					<button
						onClick={() => setOpenNextSteps(true)}
						className="w-full -translate-y-28"
					>
						<div className="h-28 rounded-t-xl bg-[#d8bfa1]">
							<div className="mx-auto h-1 w-12 translate-y-2 bg-black opacity-10" />
							<h2 className="pt-5 font-bold uppercase tracking-widest">
								Next Steps
							</h2>
						</div>
					</button>
					<button
						onClick={() => setOpenUpcomingEvents(true)}
						className="w-full -translate-y-40"
					>
						<div className="h-28 rounded-t-xl bg-[#ded9d3] sm:h-20">
							<div className="mx-auto h-1 w-12 translate-y-2 bg-black opacity-10" />
							<h2 className="pt-5 font-bold uppercase tracking-widest">
								Upcoming Events
							</h2>
						</div>
					</button>
				</div>
				<AnimatePresence>
					{openLatestMessages && (
						<ShowLatestMessages onClose={() => setOpenLatestMessages(false)} />
					)}
					{openGiving && <ShowGiving onClose={() => setOpenGiving(false)} />}
					{openNextSteps && (
						<ShowNextSteps onClose={() => setOpenNextSteps(false)} />
					)}
					{openUpcomingEvents && (
						<ShowUpcomingEvents onClose={() => setOpenUpcomingEvents(false)} />
					)}
				</AnimatePresence>
			</div>
		</div>
	)
}

function ShowLatestMessages({ onClose }: { onClose: () => void }) {
	const { message } = useLoaderData<typeof loader>()

	return (
		<Modal onClose={onClose} classNames={['bg-gradient-to-b from-zinc-900 to-black']}>
			<div className="flex h-full flex-col pt-3 text-white">
				<div className="px-3 pb-4">
					<div className="relative mt-5 text-center">
						<span className="font-bold uppercase tracking-widest">
							Latest Message
						</span>
						<div className="absolute inset-y-0 right-0">
							<button className="mr-1 text-gray-400 hover:text-white focus:outline-none" onClick={onClose}>
								Close
							</button>
						</div>
					</div>
				</div>

				<div className="flex-1 overflow-y-scroll px-6">
					{message ? (
						<div className="pb-12">
							{/* Message Card */}
							<div className="mt-4 rounded-2xl bg-gradient-to-br from-zinc-800/80 to-zinc-900/80 p-6 shadow-xl ring-1 ring-white/10">
								{/* Subtitle/Series */}
								<p className="text-xs font-medium uppercase tracking-widest text-amber-400/90">
									{message.subtitle}
								</p>

								{/* Title */}
								<h3 className="mt-3 text-2xl font-semibold tracking-widest text-white sm:text-3xl">
									{message.title}
								</h3>

								{/* Author */}
								<p className="mt-2 text-sm text-gray-400">
									by <span className="font-medium text-gray-300">{message.author}</span>
								</p>

								{/* Description */}
								{message.content && (
									<p className="mt-4 text-sm leading-relaxed text-gray-300">
										{message.content}
									</p>
								)}
							</div>

							{/* Action Buttons */}
							<div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
								<a
									href={message.video}
									target="_blank"
									rel="noreferrer"
									className="flex items-center justify-center gap-3 rounded-xl bg-white px-6 py-3.5 font-semibold text-black transition hover:bg-gray-100"
								>
									<VideoCameraIcon className="h-5 w-5" />
									<span>Watch Video</span>
								</a>
								<a
									href="https://hopepointchurch.org/hope-messages/"
									target="_blank"
									rel="noreferrer"
									className="flex items-center justify-center gap-3 rounded-xl bg-zinc-800 px-6 py-3.5 font-semibold text-white ring-1 ring-white/20 transition hover:bg-zinc-700"
								>
									<MicrophoneIcon className="h-5 w-5" />
									<span>Listen to Podcast</span>
								</a>
							</div>

							{/* Browse All */}
							<div className="mt-8 text-center">
								<a
									href="https://hopepointchurch.org/hope-messages/"
									target="_blank"
									rel="noreferrer"
									className="text-sm text-gray-400 underline decoration-gray-600 underline-offset-4 hover:text-white hover:decoration-white"
								>
									Browse all messages →
								</a>
							</div>
						</div>
					) : (
						<div className="flex flex-col items-center justify-center py-16">
							<MicrophoneIcon className="h-12 w-12 text-gray-600" />
							<p className="mt-4 text-center text-gray-400">
								No message available yet.
							</p>
						</div>
					)}
				</div>
			</div>
		</Modal>
	)
}
function ShowGiving({ onClose }: { onClose: () => void }) {
	return (
		<Modal onClose={onClose} classNames={['bg-[#e8ddcb]']}>
			<div className="flex h-full flex-col pt-3 text-black">
				<div className="px-3 pb-4">
					<div className="relative mt-5 text-center">
						<span className="font-bold uppercase tracking-widest">Giving</span>
						<div className="absolute inset-y-0 right-0">
							<button className="mr-1 text-stone-500 hover:text-black focus:outline-none" onClick={onClose}>
								Close
							</button>
						</div>
					</div>
				</div>

				<div className="flex-1 space-y-4 overflow-y-scroll px-4 pb-24">
					{/* Giving Card */}
					<div className="rounded-2xl bg-[#d8bfa1] p-5 shadow-md ring-1 ring-black/5">
						<div className="flex items-start gap-3">
							<div className="rounded-full bg-[#d8bfa1] p-2">
								<svg className="h-5 w-5 text-stone-700" fill="currentColor" viewBox="0 0 24 24">
									<path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
								</svg>
							</div>
							<div className="flex-1">
								<h3 className="text-lg font-semibold tracking-wide text-stone-800">
									Your giving makes a difference!
								</h3>
								<p className="mt-1 text-sm leading-relaxed text-stone-600">
									Giving is at the heart of God and we are confident that you will
									discover that He refuses to be outgiven! Your gift helps us expand
									the Kingdom of God and reach as many as we can for Jesus Christ.
								</p>
								<a
									href="https://pushpay.com/g/hopepointchurch?src=hp"
									target="_blank"
									rel="noreferrer"
									className="mt-3 inline-block rounded-lg border-2 border-black bg-[#e8ddcb] px-4 py-2 text-sm font-bold transition hover:bg-[#ded9d3]"
								>
									Give Now →
								</a>
							</div>
						</div>
					</div>
				</div>
			</div>
		</Modal>
	)
}
function ShowNextSteps({ onClose }: { onClose: () => void }) {
	return (
		<Modal onClose={onClose} classNames={['bg-gradient-to-b from-[#e0cbb3] to-[#d8bfa1]']} title="Next Steps">
			<div className="flex h-full flex-col pt-3 text-black">
				<div className="px-3 pb-4">
					<div className="relative mt-5 text-center">
						<span className="font-bold uppercase tracking-widest">
							Next Steps
						</span>
						<div className="absolute inset-y-0 right-0">
							<button className="mr-1 text-stone-600 hover:text-black focus:outline-none" onClick={onClose}>
								Close
							</button>
						</div>
					</div>
				</div>

				<div className="flex-1 space-y-4 overflow-y-scroll px-4 pb-24">
					{/* Decision Card */}
					<div className="rounded-2xl bg-[#e8ddcb] p-5 shadow-md ring-1 ring-black/5">
						<div className="flex items-start gap-3">
							<div className="rounded-full bg-[#d8bfa1] p-2">
								<svg className="h-5 w-5 text-stone-700" fill="currentColor" viewBox="0 0 24 24">
									<path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
								</svg>
							</div>
							<div className="flex-1">
								<h3 className="text-lg font-semibold tracking-wide text-stone-800">
									I have decided!
								</h3>
								<p className="mt-1 text-sm leading-relaxed text-stone-600">
									We want to connect with you and walk you through your journey. Let us know that you made that decision.
								</p>
								<a
									href="https://hopepointchurch.churchcenter.com/people/forms/555152"
									target="_blank"
									rel="noreferrer"
									className="mt-3 inline-block rounded-lg border-2 border-black bg-[#ded9d3] px-4 py-2 text-sm font-bold transition hover:bg-[#d4cfca]"
								>
									I made a decision →
								</a>
							</div>
						</div>
					</div>

					{/* Baptism Card */}
					<div className="rounded-2xl bg-[#e8ddcb] p-5 shadow-md ring-1 ring-black/5">
						<div className="flex items-start gap-3">
							<div className="rounded-full bg-[#d8bfa1] p-2">
								<svg className="h-5 w-5 text-stone-700" fill="currentColor" viewBox="0 0 24 24">
									<path d="M12 2c-5.33 4.55-8 8.48-8 11.8 0 4.98 3.8 8.2 8 8.2s8-3.22 8-8.2c0-3.32-2.67-7.25-8-11.8zm0 18c-3.35 0-6-2.57-6-6.2 0-2.34 1.95-5.44 6-9.14 4.05 3.7 6 6.79 6 9.14 0 3.63-2.65 6.2-6 6.2z" />
								</svg>
							</div>
							<div className="flex-1">
								<h3 className="text-lg font-semibold tracking-wide text-stone-800">
									Baptism
								</h3>
								<p className="mt-1 text-sm leading-relaxed text-stone-600">
									Baptism is going public about your faith in Jesus and communicating your heartfelt commitment to following Him.
								</p>
								<a
									href="https://hopepointchurch.churchcenter.com/unproxy/registrations/events/category/81583"
									target="_blank"
									rel="noreferrer"
									className="mt-3 inline-block rounded-lg border-2 border-black bg-[#ded9d3] px-4 py-2 text-sm font-bold transition hover:bg-[#d4cfca]"
								>
									Sign up →
								</a>
							</div>
						</div>
					</div>

					{/* Journey Card */}
					<div className="rounded-2xl bg-[#e8ddcb] p-5 shadow-md ring-1 ring-black/5">
						<div className="flex items-start gap-3">
							<div className="rounded-full bg-[#d8bfa1] p-2">
								<svg className="h-5 w-5 text-stone-700" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" d="M9 6.75V15m6-6v8.25m.503 3.498l4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 00-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0z" />
								</svg>
							</div>
							<div className="flex-1">
								<h3 className="text-lg font-semibold tracking-wide text-stone-800">
									Journey
								</h3>
								<p className="mt-1 text-sm leading-relaxed text-stone-600">
									Get to know people, meet our pastors, and learn a little more about Hope Point Church.
								</p>
								<a
									href="https://hopepointchurch.churchcenter.com/registrations/events/category/81625"
									target="_blank"
									rel="noreferrer"
									className="mt-3 inline-block rounded-lg border-2 border-black bg-[#ded9d3] px-4 py-2 text-sm font-bold transition hover:bg-[#d4cfca]"
								>
									The Journey →
								</a>
							</div>
						</div>
					</div>

					{/* Foundations Card */}
					<div className="rounded-2xl bg-[#e8ddcb] p-5 shadow-md ring-1 ring-black/5">
						<div className="flex items-start gap-3">
							<div className="rounded-full bg-[#d8bfa1] p-2">
								<svg className="h-5 w-5 text-stone-700" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
								</svg>
							</div>
							<div className="flex-1">
								<h3 className="text-lg font-semibold tracking-wide text-stone-800">
									Foundations
								</h3>
								<p className="mt-1 text-sm leading-relaxed text-stone-600">
									A class designed to teach you eight fundamental principles that will help you build well in your faith, while connecting you with others.
								</p>
								<a
									href="https://hopepointchurch.churchcenter.com/groups/connect-groups?enrollment=open_signup%2Crequest_to_join&filter=enrollment%2Ctag&tag_id=1965766"
									target="_blank"
									rel="noreferrer"
									className="mt-3 inline-block rounded-lg border-2 border-black bg-[#ded9d3] px-4 py-2 text-sm font-bold transition hover:bg-[#d4cfca]"
								>
									Foundations →
								</a>
							</div>
						</div>
					</div>

					{/* Connect Groups Card */}
					<div className="rounded-2xl bg-[#e8ddcb] p-5 shadow-md ring-1 ring-black/5">
						<div className="flex items-start gap-3">
							<div className="rounded-full bg-[#d8bfa1] p-2">
								<svg className="h-5 w-5 text-stone-700" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
								</svg>
							</div>
							<div className="flex-1">
								<h3 className="text-lg font-semibold tracking-wide text-stone-800">
									Connect Groups
								</h3>
								<p className="mt-1 text-sm leading-relaxed text-stone-600">
									You weren't meant to do life alone; we believe we are better together. Check out all our groups!
								</p>
								<a
									href="https://hopepointchurch.org/hubs/"
									target="_blank"
									rel="noreferrer"
									className="mt-3 inline-block rounded-lg border-2 border-black bg-[#ded9d3] px-4 py-2 text-sm font-bold transition hover:bg-[#d4cfca]"
								>
									Get Connected →
								</a>
							</div>
						</div>
					</div>
				</div>
			</div>
		</Modal>
	)
}
function ShowUpcomingEvents({ onClose }: { onClose: () => void }) {
	const { events } = useLoaderData<typeof loader>()

	function formatDisplayDate(event: (typeof events)[0]): string {
		if (event.displayDate) return event.displayDate
		if (event.isRecurring) return 'Recurring'

		const start = new Date(event.start)
		const startStr = start.toLocaleDateString('en-US', {
			month: 'short',
			day: 'numeric',
			timeZone: 'UTC',
		})

		if (!event.end) return startStr

		const end = new Date(event.end)
		// Same day - just show start
		if (start.toISOString().split('T')[0] === end.toISOString().split('T')[0]) return startStr

		// Same month - show "Mar 5-7"
		if (start.getUTCMonth() === end.getUTCMonth() && start.getUTCFullYear() === end.getUTCFullYear()) {
			return `${start.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' })} ${start.getUTCDate()}-${end.getUTCDate()}`
		}

		// Different months - show "Mar 5 - Apr 7"
		const endStr = end.toLocaleDateString('en-US', {
			month: 'short',
			day: 'numeric',
			timeZone: 'UTC',
		})
		return `${startStr} - ${endStr}`
	}

	return (
		<Modal
			onClose={onClose}
			classNames={['bg-[#ded9d3]']}
			title="Upcoming Events"
		>
			<div className="flex h-full flex-col pt-3 text-black">
				<div className="px-3 pb-4 shadow-sm">
					<div className="relative mt-5 text-center">
						<span className="font-bold uppercase tracking-widest">
							Upcoming Events
						</span>
						<div className="absolute inset-y-0 right-0">
							<button className="mr-1 focus:outline-none" onClick={onClose}>
								Close
							</button>
						</div>
					</div>
				</div>

				<div className="flex-1 overflow-y-scroll pb-96">
					{events.length === 0 ? (
						<div className="px-4 py-8 text-center text-stone-600">
							No upcoming events at this time. Check back soon!
						</div>
					) : (
						events.map(event => (
							<div
								key={event.id}
								className="grid grid-cols-12 divide-x-4 divide-black px-4 py-4"
							>
								<div className="col-span-2 space-y-1 pr-1.5 text-[10px] text-stone-600">
									<div>{formatDisplayDate(event)}</div>
									{event.time && <div>{event.time}</div>}
								</div>
								<div className="col-span-10 space-y-1 pl-2 text-left">
									<div className="flex items-center justify-between">
										<div className="font-bold">{event.title}</div>
										{event.ics && (
											<a href={event.ics} download={true}>
												<svg
													xmlns="http://www.w3.org/2000/svg"
													viewBox="0 0 24 24"
													fill="currentColor"
													className="h-5 w-5"
												>
													<path d="M12.75 12.75a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM7.5 15.75a.75.75 0 100-1.5.75.75 0 000 1.5zM8.25 17.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM9.75 15.75a.75.75 0 100-1.5.75.75 0 000 1.5zM10.5 17.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM12 15.75a.75.75 0 100-1.5.75.75 0 000 1.5zM12.75 17.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM14.25 15.75a.75.75 0 100-1.5.75.75 0 000 1.5zM15 17.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM16.5 15.75a.75.75 0 100-1.5.75.75 0 000 1.5zM15 12.75a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM16.5 13.5a.75.75 0 100-1.5.75.75 0 000 1.5z" />
													<path
														fillRule="evenodd"
														d="M6.75 2.25A.75.75 0 017.5 3v1.5h9V3A.75.75 0 0118 3v1.5h.75a3 3 0 013 3v11.25a3 3 0 01-3 3H5.25a3 3 0 01-3-3V7.5a3 3 0 013-3H6V3a.75.75 0 01.75-.75zm13.5 9a1.5 1.5 0 00-1.5-1.5H5.25a1.5 1.5 0 00-1.5 1.5v7.5a1.5 1.5 0 001.5 1.5h13.5a1.5 1.5 0 001.5-1.5v-7.5z"
														clipRule="evenodd"
													/>
												</svg>
											</a>
										)}
									</div>
									<div className="text-xs text-stone-600">{event.content}</div>
									<div className="flex items-center gap-x-2">
										{event.signUp && (
											<a
												href={event.signUp}
												target="_blank"
												rel="noreferrer"
											>
												<button className="mt-3 rounded-lg border border-black px-3.5 py-1.5 text-xs font-bold uppercase">
													{event.signUpLabel || 'Sign Up'}
												</button>
											</a>
										)}
										{event.volunteerUrl && (
											<a
												href={event.volunteerUrl}
												target="_blank"
												rel="noreferrer"
											>
												<button className="mt-3 rounded-lg border border-black px-3.5 py-1.5 text-xs font-bold uppercase">
													{event.volunteerLabel || 'Volunteer'}
												</button>
											</a>
										)}
									</div>
								</div>
							</div>
						))
					)}
				</div>
			</div>
		</Modal>
	)
}

function Modal({
	onClose,
	classNames,
	children,
	title,
}: {
	onClose: () => void
	classNames: string[]
	children: any
	title?: string
}) {
	return (
		<Dialog
			className="fixed inset-0 z-10 mx-auto max-w-xl"
			onClose={onClose}
			open={true}
			static
		>
			<div className="flex h-full flex-col justify-center pt-4 text-center sm:block sm:p-0">
				<Dialog.Overlay
					as={motion.div}
					variants={{
						open: {
							opacity: 1,
							transition: {
								ease: TRANSITIONS.EASE,
								duration: 2,
							},
						},
						closed: {
							opacity: 0,
							transition: {
								ease: TRANSITIONS.EASE,
								duration: TRANSITIONS.DURATION,
							},
						},
					}}
					initial={{ opacity: 0 }}
					animate={{
						opacity: 1,
						transition: { duration: 0.4, ease: [0.36, 0.66, 0.04, 1] },
					}}
					exit={{
						opacity: 0,
						transition: { duration: 0.3, ease: [0.36, 0.66, 0.04, 1] },
					}}
				// className="fixed inset-0 bg-black"
				/>

				<motion.div
					initial={{ y: '100%' }}
					animate={{
						y: 0,
						transition: { duration: 0.4, ease: [0.36, 0.66, 0.04, 1] },
					}}
					exit={{
						y: '100%',
						transition: { duration: 0.3, ease: [0.36, 0.66, 0.04, 1] },
					}}
					className={`${title ? 'mt-32' : 'mt-16'
						} z-0 flex h-full w-full flex-col rounded-t-xl sm:mt-20 ${classNames.join(
							' ',
						)}`}
				>
					{children}
				</motion.div>
			</div>
		</Dialog>
	)
}